import { Link } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatDateTime } from '@/lib/format';
import { formatMinor } from '@/lib/money';
import { FINANCE_ATTEMPT_STATUS_TONE } from '@/lib/status-tone';
import type { FinanceProviderEventsPage, FinanceProviderFact, FinanceRefundAttempt } from '@/api/generated/types.gen';

/** The income-side cutover's resolution for pre-baseline facts (Go:
 * payment.LegacyCutoverResolution). Such a fact cannot be unresolved. */
const LEGACY_CUTOVER_RESOLUTION = 'legacy: before import_cutoff';

export interface ProviderEventsTableProps {
  data: FinanceProviderEventsPage;
  /** finance.confirm — gates every action column (I12); read-only otherwise. */
  canConfirm: boolean;
  /**
   * D-M2 (final review): the Settle/Resolve/Unresolve actions on a
   * provider_queue FACT (capture/refund PSP money with no schedule row
   * able to settle it — no booking at all, or an owner whose only row
   * cannot, OH-183 item 7) are server-gated on income_side_enabled
   * (settleFinanceProviderPayment/…Refund, resolve/unresolve) — every
   * no-booking capture backfilled by 0076 answered 409
   * income_side_disabled pre-cutover, with Resolve showing the raw server
   * detail. Hidden here when the flag is off, same as PaymentsCard's
   * Confirm receipt. The refund-attempt actions below (unpaired/
   * contradictions/stale — match/attach/abandon/split/write off) are the
   * OLDER refund protocol, unaffected by this flag, and stay as is.
   */
  incomeSideEnabled: boolean;
  processed?: 'none' | 'resolved';
  onSettleFact: (fact: FinanceProviderFact) => void;
  onResolveFact: (fact: FinanceProviderFact) => void;
  onUnresolveFact: (fact: FinanceProviderFact) => void;
  /** "Match as ours" (unpaired) / "Found in console…" (stale unknown) — both ask for a provider_ref and call useRefundMatch. */
  onMatchWithRef: (attempt: FinanceRefundAttempt) => void;
  /** "Re-read provider" (contradictions) — useRefundMatch with no ref. */
  onReread: (attempt: FinanceRefundAttempt) => void;
  onAttach: (attempt: FinanceRefundAttempt) => void;
  onNeverExecuted: (attempt: FinanceRefundAttempt) => void;
  /** Only offered when the attempt already carries a refund_id — Split/Write off act on THIS attempt's own paired obligation, not any obligation of the payment (GET /finance/refunds?payment_id= exists now, but picking a different obligation than the one the operator is looking at here would be a different, more confusing action). */
  onSplit: (attempt: FinanceRefundAttempt) => void;
  onWriteOff: (attempt: FinanceRefundAttempt) => void;
}

function SectionHeading({ children, description }: { children: React.ReactNode; description: string }) {
  return (
    <div className="mb-3">
      <h3 className="text-base font-semibold text-[var(--text-heading)]">{children}</h3>
      <p className="mt-1 text-sm text-muted">{description}</p>
    </div>
  );
}

/**
 * The provider-events screen's five sections (spec §9, provider-events
 * endpoint): the third-source queue itself (provider_queue), attempts
 * needing a human decision (unpaired/contradictions/stale), and read-only
 * anomalies. One table component per section rather than one big table —
 * the columns and actions genuinely differ (a fact settles/resolves; an
 * attempt matches/attaches/abandons), and a shared table would need a
 * column union most rows leave blank.
 */
export function ProviderEventsTable({
  data,
  canConfirm,
  incomeSideEnabled,
  processed = 'none',
  onSettleFact,
  onResolveFact,
  onUnresolveFact,
  onMatchWithRef,
  onReread,
  onAttach,
  onNeverExecuted,
  onSplit,
  onWriteOff,
}: ProviderEventsTableProps) {
  const sections = [
    { key: 'provider_queue', label: 'Provider payments', count: data.provider_queue.length },
    { key: 'unpaired', label: 'Unmatched refunds', count: data.unpaired.length },
    { key: 'contradictions', label: 'Conflicting statuses', count: data.contradictions.length },
    { key: 'stale', label: 'Delayed refunds', count: data.stale.length },
    { key: 'anomalies', label: 'Amount discrepancies', count: data.anomalies.length },
  ];
  const populated = sections.filter((section) => section.count > 0);
  if (populated.length === 0) {
    return (
      <div role="status" className="rounded-[var(--radius-card)] border border-dashed border-[var(--border-hairline)] bg-card px-6 py-12 text-center">
        <h3 className="font-semibold text-[var(--text-heading)]">{processed === 'resolved' ? 'No resolved provider payments' : 'No open reconciliation items'}</h3>
        <p className="mt-2 text-sm text-muted">{processed === 'resolved' ? 'Payments closed without a transaction will appear here.' : 'There are no provider payments or refund issues to review in this list.'}</p>
      </div>
    );
  }
  return (
    <div className="flex min-w-0 flex-col gap-6">
      <nav aria-label="Reconciliation categories" className="flex flex-wrap gap-2">
        {populated.map((section) => (
          <a key={section.key} href={`#reconcile-${section.key}`} className="rounded-full border border-[var(--border-hairline)] bg-card px-3 py-2 text-sm text-primary hover:bg-[var(--surface-sunken)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {section.label} <span className="ml-2 font-semibold tabular-nums">{section.count}</span>
          </a>
        ))}
      </nav>
      {data.provider_queue.length > 0 && <section id="reconcile-provider_queue" className="scroll-mt-6">
        <SectionHeading description="Match money moved by the provider to an account transaction. Resolve only when no transaction should be recorded.">Provider payments ({data.provider_queue.length})</SectionHeading>
        <ul className="divide-y divide-[var(--border-hairline)] rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
            {data.provider_queue.map((fact) => (
              <li key={fact.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm">
                <div className="min-w-0 break-words">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={fact.kind === 'refund' ? 'danger' : 'neutral'} size="sm">
                      {fact.kind === 'refund' ? 'Refund' : 'Payment'}
                    </Badge>
                    <span className="tabular-nums font-semibold">{formatMinor(fact.amount_minor, fact.currency)}</span>
                    <span className="text-xs text-muted">{formatDateTime(fact.occurred_at)}</span>
                    {fact.provider_fee_minor ? (
                      <span className="text-xs text-muted">fee {formatMinor(fact.provider_fee_minor, fact.currency)}</span>
                    ) : null}
                  </div>
                  {fact.processed === 'settled' && fact.settled_movement_id && (
                    <p className="text-xs text-muted">
                      Settled —{' '}
                      <Link to="/finance/movements/$id" params={{ id: fact.settled_movement_id }} className="text-primary hover:underline">
                        view movement
                      </Link>
                    </p>
                  )}
                  {fact.processed === 'resolved' && fact.resolution && (
                    <p className="text-xs text-muted">Resolved — {fact.resolution}</p>
                  )}
                </div>
                {/* Ruling FR-1/FR-11/FR-14 (D-I2, re-review R3): Settle and Resolve
                    act only on money the provider already moved — for a refund
                    fact, an EXECUTED obligation (the server answers 422
                    not_executed otherwise). The queue also lists open, not yet
                    covered obligations: spec §10.4 shows them as waiting for the
                    provider, their actions are attempts/split/write-off/abandon
                    (sections below). Captures unchanged. */}
                {canConfirm && incomeSideEnabled && fact.processed === 'none' &&
                  (fact.kind !== 'refund' || fact.refund_status === 'executed') && (
                    <span className="flex gap-2">
                      <Button size="sm" variant="primary" onClick={() => onSettleFact(fact)}>
                        Settle
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => onResolveFact(fact)}>
                        Resolve
                      </Button>
                    </span>
                  )}
                {fact.processed === 'none' && fact.kind === 'refund' && fact.refund_status === 'open' && (
                  <span className="text-xs text-muted">Waiting for provider</span>
                )}
                {/* Ruling FR-15: the cutover's legacy marker is reserved to the cutover —
                    the server refuses to unresolve such a fact (422 not_a_target). */}
                {canConfirm && incomeSideEnabled && fact.processed === 'resolved' &&
                  fact.resolution !== LEGACY_CUTOVER_RESOLUTION && (
                  <Button size="sm" variant="secondary" onClick={() => onUnresolveFact(fact)}>
                    Unresolve
                  </Button>
                )}
              </li>
            ))}
        </ul>
      </section>}

      {data.unpaired.length > 0 && <section id="reconcile-unpaired" className="scroll-mt-6">
        <SectionHeading description="Link each provider refund to the refund it belongs to, or identify its provider reference.">Unmatched refunds ({data.unpaired.length})</SectionHeading>
        <ul className="divide-y divide-[var(--border-hairline)] rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
            {data.unpaired.map((a) => (
              <AttemptRow key={a.id} attempt={a}>
                {canConfirm && (
                  <span className="flex flex-wrap gap-2">
                    <Button size="sm" variant="primary" onClick={() => onAttach(a)}>
                      Attach to obligation
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => onMatchWithRef(a)}>
                      Match as ours
                    </Button>
                  </span>
                )}
              </AttemptRow>
            ))}
        </ul>
      </section>}

      {data.contradictions.length > 0 && <section id="reconcile-contradictions" className="scroll-mt-6">
        <SectionHeading description="Check the latest provider status before changing the refund.">Conflicting statuses ({data.contradictions.length})</SectionHeading>
        <ul className="divide-y divide-[var(--border-hairline)] rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
            {data.contradictions.map((a) => (
              <AttemptRow key={a.id} attempt={a}>
                {canConfirm && (
                  <span className="flex flex-wrap gap-2">
                    <Button size="sm" variant="primary" onClick={() => onReread(a)}>
                      Re-read provider
                    </Button>
                    {a.refund_id && (
                      <>
                        <Button size="sm" variant="secondary" onClick={() => onSplit(a)}>
                          Split
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => onWriteOff(a)}>
                          Write off
                        </Button>
                      </>
                    )}
                  </span>
                )}
              </AttemptRow>
            ))}
        </ul>
      </section>}

      {data.stale.length > 0 && <section id="reconcile-stale" className="scroll-mt-6">
        <SectionHeading description="Check the provider console to establish whether the refund happened before taking another action.">Delayed refunds ({data.stale.length})</SectionHeading>
        <ul className="divide-y divide-[var(--border-hairline)] rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
            {data.stale.map((a) => (
              <AttemptRow key={a.id} attempt={a}>
                {canConfirm && a.status === 'unknown' && (
                  <span className="flex flex-wrap gap-2">
                    <Button size="sm" variant="primary" onClick={() => onMatchWithRef(a)}>
                      Found in console…
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => onNeverExecuted(a)}>
                      Never executed
                    </Button>
                  </span>
                )}
                {canConfirm && a.status !== 'unknown' && a.refund_id && (
                  <span className="flex flex-wrap gap-2">
                    <Button size="sm" variant="secondary" onClick={() => onSplit(a)}>
                      Split
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => onWriteOff(a)}>
                      Write off
                    </Button>
                  </span>
                )}
              </AttemptRow>
            ))}
        </ul>
      </section>}

      {data.anomalies.length > 0 && <section id="reconcile-anomalies" className="scroll-mt-6">
        <SectionHeading description="Review possible overpayments or duplicate refunds. These records need investigation.">Amount discrepancies ({data.anomalies.length})</SectionHeading>
        <ul className="divide-y divide-[var(--border-hairline)] rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
            {data.anomalies.map((an) => (
              <li key={an.decision_id} className="flex flex-col gap-1 px-4 py-3 text-sm">
                <div className="flex items-center gap-2">
                  <Badge variant="danger" size="sm">
                    {an.kind === 'overpayment' ? 'Overpayment' : 'Double execution'}
                  </Badge>
                  <span className="tabular-nums font-semibold">{formatMinor(an.amount_minor, an.currency)}</span>
                </div>
                {an.facts && an.facts.length > 0 && (
                  <p className="text-xs text-muted">Facts: {an.facts.join(', ')}</p>
                )}
              </li>
            ))}
        </ul>
      </section>}
    </div>
  );
}

function AttemptRow({ attempt, children }: { attempt: FinanceRefundAttempt; children?: React.ReactNode }) {
  const tone = FINANCE_ATTEMPT_STATUS_TONE[attempt.status];
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm">
      <div className="min-w-0 break-words">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={tone.variant} size="sm">
            {tone.label}
          </Badge>
          <span className="tabular-nums font-semibold">{formatMinor(attempt.amount_minor, attempt.currency)}</span>
          {attempt.provider_ref && <span className="text-xs text-muted">ref {attempt.provider_ref}</span>}
        </div>
        <p className="text-xs text-muted">
          {attempt.first_sent_at ? `Sent ${formatDateTime(attempt.first_sent_at)}` : `Created ${formatDateTime(attempt.created_at)}`}
          {attempt.retry_deadline ? ` · deadline ${formatDateTime(attempt.retry_deadline)}` : ''}
        </p>
        {attempt.error && <p className="text-xs text-destructive">{attempt.error}</p>}
      </div>
      {children}
    </li>
  );
}
